import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import SvgIcon from "../../../../../parts/SvgIcon";
import Toast from "../../../../../parts/toast";

const DataSourceModal = ({ locals, initialSources, onClose, onSaved }) => {
  const { t } = useTranslation("athlete-portal");
  const activeUser = locals?.activeUserToRequest || locals?.activeUser || {};
  const { _id: userId } = activeUser;
  const mealPlanDataSources = initialSources ||
    activeUser.mealPlanDataSources || ["usda", "afcd", "verified-food"];
  const fixedSources = [
    {
      name: t("dataSourceModal.customFoods"),
      description: t("dataSourceModal.customFoodsDescription"),
    },
    {
      name: t("dataSourceModal.recipes"),
      description: t("dataSourceModal.recipesDescription"),
    },
  ];

  const selectableSources = [
    {
      value: "verified-food",
      label: "MSB Verified Foods",
      description:
        "A curated library of verified whole foods designed to keep your nutrition data clean, accurate, and easy to use.",
    },
    {
      value: "usda",
      label: "USDA",
      description: t("dataSourceModal.usda"),
    },
    {
      value: "afcd",
      label: "AFCD",
      description: t("dataSourceModal.afcd"),
    },
  ];

  const [selectedSources, setSelectedSources] = useState(mealPlanDataSources);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showErrorToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type: "error", message });
    toastTimerRef.current = setTimeout(() => setToast(null), 10000);
  };

  /**
   * Function to select the options
   * @param {string} value - slug sent to/stored by backend
   */
  const handleSelectionChange = (value) => {
    setSelectedSources((prevSelected) =>
      prevSelected.includes(value)
        ? prevSelected.filter((source) => source !== value)
        : [...prevSelected, value]
    );
  };

  /**
   * Function to save the preference
   */
  const handleSend = async () => {
    const body = {
      mealPlanDataSources: selectedSources,
      userId,
    };
    const stringBody = JSON.stringify(body);

    if (onSaved) {
      setIsSaving(true);
      try {
        const res = await fetch(
          "/actions/user/meal-plan-data-source-preference",
          {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: stringBody,
          }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.error || t("dataSourceModal.saveFailed"));
        }
        onSaved(data.mealPlanDataSources || selectedSources, data.message);
      } catch (err) {
        console.error("Error saving data source preference:", err);
        showErrorToast(err.message || t("dataSourceModal.saveFailed"));
      } finally {
        setIsSaving(false);
      }
      return;
    }

    submit("/actions/user/edit-meal-plan-data-source-preference", {
      method: "POST",
      body: { stringBody },
    });
  };

  return (
    <>
      <Modal
        className="data-source-modal"
        title={t("dataSourceModal.title")}
        onClose={(e) => {
          if (onClose) {
            e.preventDefault();
            onClose();
          }
        }}
      >
        <div className="body">
          <div className="description">
            <span>{t("dataSourceModal.description")}</span>
          </div>
          <div className="selection">
            <ul>
              {/* First, the default ones */}
              {fixedSources.map(({ name, description }, index) => (
                <li key={`fixed-${name}-${index}`} className="not-eligible">
                  <div className="information">
                    <span>{name}</span>
                    <span className="opacity-70">{description}</span>
                  </div>
                  <div className="checkbox">
                    <SvgIcon
                      src={`/img/new-icons/checked-square.svg`}
                      noFilterOnLight
                    />
                  </div>
                </li>
              ))}

              {/* Then, the eligible */}
              {selectableSources.map(({ value, label, description }, index) => (
                <li key={`${value}-${index}`}>
                  <div className="information">
                    <span>{label}</span>
                    <span className="opacity-70">{description}</span>
                  </div>
                  <div className="checkbox">
                    <SvgIcon
                      onClick={() => handleSelectionChange(value)}
                      src={`/img/new-icons/${
                        selectedSources.includes(value)
                          ? "checked-square.svg"
                          : "unchecked-square.svg"
                      }`}
                      noFilterOnLight={
                        selectedSources.includes(value) ? true : false
                      }
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer">
            {onClose && (
              <button
                type="button"
                data-button-type="secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                {t("dataSourceModal.cancel", "Cancel")}
              </button>
            )}
            <button
              type="button"
              data-button-type="primary"
              onClick={handleSend}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : t("dataSourceModal.save")}
            </button>
          </div>
        </div>
      </Modal>
      <Toast
        open={Boolean(toast)}
        type={toast?.type ?? "error"}
        message={toast?.message ?? ""}
      />
    </>
  );
};

export default DataSourceModal;
